/**
 * Date and time utilities for frontend with dayjs plugin setup.
 * 
 * This module provides timezone-aware date handling that mirrors the backend
 * utils/timezones.py API. All functions use dayjs with UTC and timezone plugins.
 */

import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter.js';
import utc from 'dayjs/plugin/utc.js';
import timezone from 'dayjs/plugin/timezone.js';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isSameOrAfter);

export { dayjs };

/**
 * Parse a datetime value as UTC.
 *
 * Backend stores all datetimes as naive UTC.  When these arrive on the frontend
 * as ISO strings without a trailing "Z" or offset (e.g. "2026-03-09T21:00:00"),
 * `dayjs()` would interpret them in the *browser's* local timezone — which is
 * incorrect.  This helper detects naive ISO strings and forces UTC parsing.
 * Strings that already carry timezone info ("Z", "+05:30", etc.) as well as
 * `Date` objects and existing `dayjs` instances pass through unchanged.
 *
 * @param dt - dayjs object, Date, or ISO string
 * @returns dayjs object parsed in UTC
 *
 * @example
 * parseAsUtc("2026-03-09T21:00:00");          // interpreted as 21:00 UTC
 * parseAsUtc("2026-03-09T21:00:00Z");         // already UTC — unchanged
 * parseAsUtc("2026-03-09T14:00:00-07:00");    // has offset — unchanged
 * parseAsUtc(dayjs.utc("2026-03-09T21:00:00")); // already dayjs — passthrough
 */
export function parseAsUtc(dt: dayjs.Dayjs | Date | string): dayjs.Dayjs {
  if (typeof dt === 'string' && !dt.endsWith('Z') && !dt.includes('+') && !dt.includes('-', 10)) {
    return dayjs.utc(dt);
  }
  return dayjs(dt);
}

/**
 * Get current datetime in UTC as dayjs object.
 * 
 * @returns dayjs object representing current time in UTC
 * 
 * @example
 * const now = nowUtc();
 * console.log(now.format()); // "2025-11-21T05:00:00Z"
 */
export function nowUtc(): dayjs.Dayjs {
  return dayjs().utc();
}

export function isWithinPastDays(dt: dayjs.Dayjs | Date | string, days: number): boolean {
  const parsed = parseAsUtc(dt);
  const now = nowUtc();
  return parsed.isValid() && parsed.isSameOrAfter(now.subtract(days, 'day'));
}

/**
 * Get current datetime as ISO 8601 string in UTC.
 * 
 * @returns ISO 8601 string with Z suffix (includes milliseconds)
 * 
 * @example
 * const timestamp = nowISO();
 * // "2025-11-21T05:00:00.123Z"
 */
export function nowISO(): string {
  return dayjs().utc().toISOString();
}

/**
 * Extract the date portion (YYYY-MM-DD) from an ISO datetime string.
 *
 * Unlike raw `.split('T')[0]`, this parses through dayjs in UTC so the
 * extracted date is always the UTC date — preventing off-by-one errors
 * near midnight when the browser's local timezone differs from UTC.
 *
 * Returns an empty string for null/undefined input.
 *
 * @param dt - ISO 8601 string, or null/undefined
 * @returns Date string in YYYY-MM-DD format, or ""
 *
 * @example
 * toDateOnly("2025-11-21T23:30:00Z"); // "2025-11-21"
 * toDateOnly(null);                    // ""
 */
export function toDateOnly(dt: string | null | undefined): string {
  if (!dt) return '';
  return dayjs.utc(dt).format('YYYY-MM-DD');
}

/**
 * Get current date in local browser time as Date object.
 * 
 * Use this for UI purposes where "today" should reflect the user's local timezone:
 * - Calendar view anchoring (which week/month to display)
 * - "Today" button functionality
 * - Highlighting the current day in calendar grids
 * - UI greeting messages (morning/afternoon/evening)
 * 
 * DO NOT use this for:
 * - API requests or data persistence (use nowUtc() or nowISO())
 * - Analytics date ranges (use appropriate timezone-aware helpers)
 * - Appointment scheduling (use location/org timezone helpers)
 * 
 * @returns Date object representing current time in local browser timezone
 * 
 * @example
 * const today = todayLocalDate();
 * setCurrentDate(today); // Initialize calendar to today
 * 
 * @example
 * const isToday = isSameDay(someDate, todayLocalDate());
 */
export function todayLocalDate(): Date {
  return dayjs().toDate();
}

/**
 * Get current date in local browser time as YYYY-MM-DD string.
 * 
 * Use this for form submissions where the date should reflect the user's local timezone:
 * - Screening administered dates
 * - Form submission dates
 * - Any date field that should represent "today" from the user's perspective
 * 
 * @returns Date string in YYYY-MM-DD format representing today in local browser timezone
 * 
 * @example
 * const today = todayLocalDateString();
 * // "2025-11-21" (user's local date)
 */
export function todayLocalDateString(): string {
  return dayjs().format('YYYY-MM-DD');
}

/**
 * Compute a date string exactly one year after the given date (or today),
 * handling the Feb 29 → Feb 28 leap year edge case.
 *
 * JavaScript's `Date.setFullYear()` silently rolls Feb 29 to Mar 1 in
 * non-leap years.  This helper clamps to Feb 28 instead, matching the
 * backend behaviour in `migration_step_handlers.py`.
 */
export function datePlusOneYear(base?: Date): string {
  const d = base ? new Date(base) : todayLocalDate();
  const origMonth = d.getMonth();
  d.setFullYear(d.getFullYear() + 1);
  // If month rolled (e.g. Feb 29 → Mar 1), clamp back to last day of Feb
  if (d.getMonth() !== origMonth) {
    d.setDate(0); // setDate(0) → last day of previous month
  }
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Parse ISO 8601 string to dayjs object in UTC.
 * 
 * @param s - ISO 8601 string (e.g., "2025-11-15T14:30:00Z")
 * @returns dayjs object in UTC
 * 
 * @example
 * const dt = parseISO("2025-11-15T14:30:00Z");
 * console.log(dt.format()); // "2025-11-15T14:30:00Z"
 */
export function parseISO(s: string): dayjs.Dayjs {
  return dayjs(s).utc();
}

/**
 * Format datetime as ISO 8601 string with Z suffix (UTC).
 * 
 * @param dt - dayjs object or Date
 * @returns ISO 8601 string with Z suffix (no milliseconds)
 * 
 * @example
 * const dt = dayjs("2025-11-15 14:30");
 * formatISO(dt); // "2025-11-15T14:30:00Z"
 */
export function formatISO(dt: dayjs.Dayjs | Date): string {
  return dayjs(dt).utc().format('YYYY-MM-DDTHH:mm:ss[Z]');
}

/**
 * Build a timezone-aware dayjs object from separate date and time strings.
 *
 * Use this whenever you have a date string ("2026-02-12") and a time string
 * ("09:00") that represent a wall-clock time in the **organization's timezone**
 * (not the browser's timezone). The returned dayjs object is timezone-aware
 * and will convert correctly to UTC via formatISO().
 *
 * @param date - Date string in YYYY-MM-DD format
 * @param time - Time string in HH:mm format
 * @param tzString - IANA timezone string (e.g., "America/Denver")
 * @returns dayjs object interpreted in the given timezone
 *
 * @example
 * // Browser is in UTC, org is America/Denver (UTC-7)
 * const dt = buildDateTimeInTz("2026-02-12", "09:00", "America/Denver");
 * formatISO(dt); // "2026-02-12T16:00:00Z" (9 AM Denver = 4 PM UTC)
 */
export function buildDateTimeInTz(
  date: string,
  time: string,
  tzString: string
): dayjs.Dayjs {
  return dayjs.tz(`${date}T${time}`, tzString);
}

export function isValidDateTimeInput(
  date: string,
  time: string,
  tzString: string
): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) {
    return false;
  }
  try {
    return buildDateTimeInTz(date, time, tzString).isValid();
  } catch {
    return false;
  }
}

/**
 * Convert UTC datetime to specified timezone.
 * 
 * @param dt - dayjs object or Date (assumed UTC if naive)
 * @param tzString - IANA timezone string (e.g., "America/Denver")
 * @returns dayjs object in specified timezone
 * 
 * @example
 * const utcDt = dayjs.utc("2025-11-15 14:30");
 * const denverDt = toOrgTz(utcDt, "America/Denver");
 * console.log(denverDt.format("h:mm A z")); // "7:30 AM MST"
 */
export function toOrgTz(dt: dayjs.Dayjs | Date, tzString: string): dayjs.Dayjs {
  try {
    return dayjs(dt).tz(tzString);
  } catch {
    // Fallback to UTC only for truly invalid timezone strings (e.g., "invalid_tz").
    // Real IANA timezones like "America/Denver" (MST) always work — no behavior change.
    return dayjs(dt).utc();
  }
}

/**
 * Format date for display in the given timezone (location > org > default).
 * 
 * Shows the timezone abbreviation when it differs from the user's browser
 * timezone, so travelers always know which timezone they're looking at.
 * 
 * @param dt - dayjs object, Date, or ISO string
 * @param tzString - IANA timezone string (use location tz, fall back to org tz)
 * @param format - dayjs format string (default: "MMM D, YYYY")
 * @returns Formatted date string, with tz abbreviation if different from browser
 * 
 * @example
 * // Browser in America/Los_Angeles, displaying Mountain time:
 * formatDateDisplay("2025-11-15T14:30:00Z", "America/Denver");
 * // "Nov 15, 2025 (MST)"
 * 
 * // Browser in America/Denver, displaying Mountain time:
 * formatDateDisplay("2025-11-15T14:30:00Z", "America/Denver");
 * // "Nov 15, 2025"
 */
export function formatDateDisplay(
  dt: dayjs.Dayjs | Date | string,
  tzString: string,
  format: string = "MMM D, YYYY"
): string {
  try {
    const tzDt = parseAsUtc(dt).tz(tzString);
    const formatted = tzDt.format(format);
    const suffix = getTimezoneSuffixIfDifferent(tzDt.toDate(), tzString);
    return suffix ? `${formatted} (${suffix})` : formatted;
  } catch {
    return dayjs(dt).utc().format(format);
  }
}

/**
 * Parse a user-typed date of birth into the canonical ISO storage format.
 *
 * Accepts ``mm/dd/yyyy``, ``mm-dd-yyyy``, and ``yyyy-mm-dd``.
 * Returns ``null`` for empty/invalid input or dates outside a reasonable range.
 *
 * @param value - raw user input
 * @returns ISO date string ``yyyy-mm-dd`` or null
 *
 * @example
 * parseDobInput("06/15/1990"); // "1990-06-15"
 * parseDobInput("1990-06-15"); // "1990-06-15"
 * parseDobInput("06-15-1990"); // "1990-06-15"
 * parseDobInput("abc");        // null
 */
export function parseDobInput(value: string | null | undefined): string | null {
  if (!value || !value.trim()) return null;
  const v = value.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
  const m = v.match(/^(\d{1,2})([/-])(\d{1,2})\2(\d{4})$/);
  if (!m) return null;
  const month = parseInt(m[1], 10);
  const day = parseInt(m[3], 10);
  const year = parseInt(m[4], 10);
  if (month < 1 || month > 12 || day < 1 || day > 31 || year < 1850) return null;
  const d = new Date(year, month - 1, day);
  if (d.getMonth() + 1 !== month || d.getDate() !== day || d.getFullYear() !== year) return null;
  const today = new Date();
  if (d > today) return null;
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/**
 * Format a stored ISO date of birth for user-facing display.
 *
 * @param value - ISO string or user-typed date
 * @returns ``mm/dd/yyyy`` string, or null if empty
 *
 * @example
 * formatDobDisplay("1990-06-15"); // "06/15/1990"
 */
export function formatDobDisplay(value: string | null | undefined): string | null {
  const iso = parseDobInput(value);
  if (!iso) return value || null;
  const [yyyy, mm, dd] = iso.split('-');
  return `${mm}/${dd}/${yyyy}`;
}

/**
 * Format time for display in the given timezone (location > org > default).
 * 
 * Shows the timezone abbreviation when it differs from the user's browser
 * timezone, so travelers always know which timezone they're looking at.
 * 
 * @param dt - dayjs object, Date, or ISO string
 * @param tzString - IANA timezone string (use location tz, fall back to org tz)
 * @param format - dayjs format string (default: "h:mm A")
 * @returns Formatted time string, with tz abbreviation if different from browser
 * 
 * @example
 * // Browser in America/Los_Angeles, displaying Mountain time:
 * formatTimeDisplay("2025-11-15T14:30:00Z", "America/Denver");
 * // "7:30 AM MST"
 * 
 * // Browser in America/Denver, displaying Mountain time:
 * formatTimeDisplay("2025-11-15T14:30:00Z", "America/Denver");
 * // "7:30 AM"
 */
export function formatTimeDisplay(
  dt: dayjs.Dayjs | Date | string,
  tzString: string,
  format: string = "h:mm A"
): string {
  try {
    const tzDt = parseAsUtc(dt).tz(tzString);
    const formatted = tzDt.format(format);
    const suffix = getTimezoneSuffixIfDifferent(tzDt.toDate(), tzString);
    return suffix ? `${formatted} ${suffix}` : formatted;
  } catch {
    return dayjs(dt).utc().format(format);
  }
}

/**
 * Parse an appointment's stored start timestamp and project it into the
 * appointment/location effective timezone (location > org > default).
 *
 * Prefer this helper whenever displaying an appointment's date or time so
 * the same instant is represented consistently across the UI.
 */
export function getAppointmentDayjs(
  appt: {
    scheduled_start?: string;
    start_time?: string;
    date?: string;
    location_timezone?: string | null;
  },
  orgTimezone: string
): dayjs.Dayjs {
  const tz = getTimezoneForAppointment(appt.location_timezone, orgTimezone);
  const raw = appt.scheduled_start || appt.start_time || appt.date || '';
  return parseAsUtc(raw).tz(tz);
}

/**
 * Get timezone for appointment (mirrors backend precedence).
 * 
 * Priority: Location timezone > Organization timezone > default
 * 
 * @param locationTz - Location's timezone
 * @param orgTz - Organization's timezone
 * @param defaultTz - System default (default: "America/Denver")
 * @returns IANA timezone string
 * 
 * @example
 * const tz = getTimezoneForAppointment("America/Los_Angeles", "America/Denver");
 * // "America/Los_Angeles"
 */
export function getTimezoneForAppointment(
  locationTz: string | null | undefined,
  orgTz: string | null | undefined,
  defaultTz: string = "America/Denver"
): string {
  return locationTz || orgTz || defaultTz;
}

/**
 * Format datetime for display with timezone abbreviation.
 * 
 * @param dt - dayjs object, Date, or ISO string
 * @param tzString - IANA timezone string
 * @returns Formatted datetime string with timezone (e.g., "Nov 15, 2025 at 7:30 AM MST")
 * 
 * @example
 * formatDateTimeWithTimezone("2025-11-15T14:30:00Z", "America/Denver");
 * // "Nov 15, 2025 at 7:30 AM MST"
 */
export function formatDateTimeWithTimezone(
  dt: dayjs.Dayjs | Date | string,
  tzString: string
): string {
  const tzDt = parseAsUtc(dt).tz(tzString);
  const dateTimeStr = tzDt.format("MMM D, YYYY [at] h:mm A");
  const tzAbbr = getTimezoneAbbr(tzDt.toDate(), tzString);
  return `${dateTimeStr} ${tzAbbr}`;
}

/**
 * Get timezone abbreviation using Intl API.
 * 
 * @param date - Date object to get timezone for
 * @param tzString - IANA timezone string
 * @returns Timezone abbreviation (e.g., "MST", "EST", "PST")
 */
function getTimezoneAbbr(date: Date, tzString: string): string {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: tzString,
    timeZoneName: 'short'
  });
  const parts = formatter.formatToParts(date);
  const tzPart = parts.find(p => p.type === 'timeZoneName');
  return tzPart?.value || tzString;
}

/**
 * Get timezone abbreviation ONLY when it differs from the browser's timezone.
 * 
 * Returns the abbreviation (e.g., "MST") if the display timezone is different
 * from the user's browser timezone. Returns null if they match — no suffix needed.
 * 
 * This ensures traveling users always see which timezone a time is displayed in,
 * while users in the same timezone as the location see clean, uncluttered times.
 */
function getTimezoneSuffixIfDifferent(date: Date, displayTz: string): string | null {
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const displayAbbr = getTimezoneAbbr(date, displayTz);
  const browserAbbr = getTimezoneAbbr(date, browserTz);
  return displayAbbr !== browserAbbr ? displayAbbr : null;
}

/**
 * Format time for display with timezone abbreviation.
 * 
 * @param dt - dayjs object, Date, or ISO string
 * @param tzString - IANA timezone string
 * @returns Formatted time string with timezone (e.g., "7:30 AM MST")
 * 
 * @example
 * formatTimeWithTimezone("2025-11-15T14:30:00Z", "America/Denver");
 * // "7:30 AM MST"
 */
export function formatTimeWithTimezone(
  dt: dayjs.Dayjs | Date | string,
  tzString: string
): string {
  const tzDt = parseAsUtc(dt).tz(tzString);
  const timeStr = tzDt.format("h:mm A");
  const tzAbbr = getTimezoneAbbr(tzDt.toDate(), tzString);
  return `${timeStr} ${tzAbbr}`;
}

/**
 * Get the timezone abbreviation for a given timezone.
 * 
 * @param tzString - IANA timezone string
 * @returns Timezone abbreviation (e.g., "MST", "EST", "PST")
 * 
 * @example
 * getTimezoneAbbreviation("America/Denver");
 * // "MST" or "MDT" depending on DST
 */
export function getTimezoneAbbreviation(tzString: string): string {
  return getTimezoneAbbr(new Date(), tzString);
}

/**
 * Convert a 24-hour time string (HH:mm) to 12-hour AM/PM format.
 * 
 * @param timeStr - Time string in 24-hour format (e.g., "09:00", "13:30")
 * @returns Time string in 12-hour format (e.g., "9:00 AM", "1:30 PM")
 * 
 * @example
 * formatTimeSlot("09:00"); // "9:00 AM"
 * formatTimeSlot("13:30"); // "1:30 PM"
 * formatTimeSlot("00:00"); // "12:00 AM"
 */
export function formatTimeSlot(timeStr: string): string {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const period = hours >= 12 ? 'PM' : 'AM';
  const displayHours = hours % 12 || 12;
  return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
}


/**
 * Calculate the number of whole days between two date strings.
 *
 * Both dates are parsed as local dates via dayjs (not UTC), so this is
 * suitable for "days since" calculations shown in the UI.
 *
 * @param fromDate - Earlier date string in YYYY-MM-DD format
 * @param toDate   - Later date string in YYYY-MM-DD format
 * @returns Number of whole days between the two dates (positive if toDate > fromDate)
 *
 * @example
 * daysBetween("2026-03-01", "2026-03-15"); // 14
 */
export function daysBetween(fromDate: string, toDate: string): number {
  return dayjs(toDate).diff(dayjs(fromDate), 'day');
}

/**
 * Extract the clinic/organization timezone from the current user object.
 *
 * Many pages need `user.organization.timezone` but the user object is loosely
 * typed, so every call-site ended up with a verbose nested type assertion.
 * This helper centralises that access pattern with a safe fallback.
 *
 * @param user - The auth user object (may be null/undefined/false)
 * @param fallback - IANA timezone to use when the value is missing (default: 'America/Denver')
 * @returns IANA timezone string
 *
 * @example
 * const tz = getClinicTimezone(user);               // 'America/Denver' if missing
 * const tz = getClinicTimezone(user, 'America/Chicago');
 */
export function getClinicTimezone(
  user: Record<string, unknown> | null | undefined | false,
  fallback = 'America/Denver',
): string {
  if (!user || typeof user !== 'object') return fallback;
  const org = user.organization;
  if (org && typeof org === 'object' && 'timezone' in (org as object)) {
    const tz = (org as Record<string, unknown>).timezone;
    if (typeof tz === 'string' && tz.length > 0) return tz;
  }
  return fallback;
}

/**
 * Check whether a UTC datetime string is in the past.
 *
 * Used to gate calendar actions (edit, cancel, reinstate, move) with a
 * confirmation dialog so users acknowledge they are modifying a past
 * appointment.  Triggers for any appointment whose scheduled start has
 * already passed.
 *
 * @param scheduledStartUtc - ISO datetime string (naive UTC from the backend)
 * @returns true if the appointment's scheduled start is before the current time
 */
export function isPastAppointment(scheduledStartUtc: string | null | undefined): boolean {
  if (!scheduledStartUtc) return false;
  const start = parseAsUtc(scheduledStartUtc);
  return dayjs.utc().isAfter(start);
}
